import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import BannerVideo from "../assets/Banner/banner-video.mp4";

import "../styles/BannerCarousel.css";

/* ── Slides ── */
const SLIDES = [
  {
    eyebrow: "HVACR • Refrigeration • Industrial Cooling",
    title: ["Advanced HVAC&R", "Systems Built for"],
    highlight: "Modern Industry",
    description:
      "Premium refrigeration tools, HVAC chemicals, leak monitoring systems, and energy-efficient cooling technologies engineered for reliability and industrial-grade performance.",
  },
  {
    eyebrow: "Control Systems • Sensors • Power Electronics",
    title: ["Precision Industrial", "Electronics for"],
    highlight: "Smarter Operations",
    description:
      "Control modules, embedded automation, power converters, and sensor arrays built for demanding industrial environments where accuracy and uptime are non-negotiable.",
  },
  {
    eyebrow: "Thermal Management • Energy Optimization",
    title: ["Energy & Efficiency", "Solutions Engineered for"],
    highlight: "Peak Performance",
    description:
      "Phase-change materials, thermal management systems, and efficiency optimization components designed to cut energy waste and maximize system output.",
  },
  {
    eyebrow: "Diagnostics • Field Tools • Calibration",
    title: ["Complete Service &", "Maintenance Ecosystem for"],
    highlight: "Field Professionals",
    description:
      "Diagnostic instruments, calibration equipment, and technician-grade repair tools that keep industrial systems running at full capacity with minimal downtime.",
  },
];

const AUTO_DELAY = 5000;

const BannerCarousel = () => {
  const navigate = useNavigate();

  const [active, setActive] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const timerRef = useRef(null);

  const goTo = useCallback((index) => {
    if (typeof index === "function") {
      setActive((prev) => {
        const next = index(prev);
        return next;
      });
    } else {
      setActive(index);
    }

    setAnimKey((k) => k + 1);
  }, []);

  const next = useCallback(() => {
    goTo((prev) => (prev + 1) % SLIDES.length);
  }, [goTo]);

  const prev = useCallback(() => {
    goTo((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, [goTo]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActive((a) => {
        const next = (a + 1) % SLIDES.length;
        setAnimKey((k) => k + 1);
        return next;
      });
    }, AUTO_DELAY);

    return () => clearInterval(timerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setActive((a) => {
        const next = (a + 1) % SLIDES.length;
        setAnimKey((k) => k + 1);
        return next;
      });
    }, AUTO_DELAY);
  }, []);

  const handleNext = () => {
    next();
    resetTimer();
  };

  const handlePrev = () => {
    prev();
    resetTimer();
  };

  const handleDot = (i) => {
    goTo(i);
    resetTimer();
  };

  const slide = SLIDES[active];

  return (
    <div className="banner-carousel">
      {/* VIDEO */}
      <div className="banner-video-wrapper">
        <video
          src={BannerVideo}
          autoPlay
          muted
          loop
          playsInline
          className="banner-video"
        />

        {/* OVERLAY */}
        <div className="banner-overlay" />

        {/* CONTENT */}
        <div key={animKey} className="banner-content">
          <div className="banner-top-bar tn-bar-anim tn-d1" />

          <div className="banner-eyebrow tn-text-anim tn-d2">
            {slide.eyebrow}
          </div>

          <h1 className="banner-title tn-text-anim tn-d3">
            {slide.title[0]}
            <br />
            {slide.title[1]}{" "}
            <span className="banner-highlight">
              {slide.highlight}
            </span>
          </h1>

          <p className="banner-description tn-text-anim tn-d4">
            {slide.description}
          </p>

          <div className="banner-buttons tn-text-anim tn-d5">
            <button
              className="banner-btn banner-btn-primary"
              onClick={() => navigate("/products")}
            >
              Explore Products
            </button>

            <button
              className="banner-btn banner-btn-secondary"
              onClick={() => navigate("/contact")}
            >
              Contact Us
            </button>
          </div>
        </div>

        {/* LEFT ARROW */}
        <button
          className="banner-arrow banner-arrow-left"
          onClick={handlePrev}
        >
          <FaChevronLeft />
        </button>

        {/* RIGHT ARROW */}
        <button
          className="banner-arrow banner-arrow-right"
          onClick={handleNext}
        >
          <FaChevronRight />
        </button>

        {/* DOTS */}
        <div className="banner-dots">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`banner-dot ${i === active ? "active" : ""
                }`}
              onClick={() => handleDot(i)}
            >
              {i === active && (
                <div
                  key={animKey}
                  className="banner-dot-fill"
                  style={{
                    animation: `tn-dot-fill ${AUTO_DELAY}ms linear forwards`,
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* COUNTER */}
        <div className="banner-counter">
          <span className="banner-counter-active">
            {String(active + 1).padStart(2, "0")}
          </span>

          <span className="banner-counter-divider">/</span>

          <span className="banner-counter-total">
            {String(SLIDES.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BannerCarousel;